import React, { FC, ReactNode } from 'react';

import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import { statFormatter } from '@nhl/formatter-utilities';
import { BaseStyleEnums, CoreUtils } from '@nhl/nhl-breakaway-ui';
import Headshot from '@nhl/nhl-react-ui/lib/Headshot';
import headshotTypes from '@nhl/nhl-react-ui/lib/Headshot/headshotTypes';

import CommonMessages from '~/core/commonMessages';
import { PlayerPosition, PlayerStatAbbrev } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { getPlayerHref, parseValueFromLocalizedAttribute } from '~/core/utils';

import { SectionHeader } from '../../styles';
import { LandingGame, ThreeStar } from '../../types';

import {
    NameContainer,
    PlayerInfo,
    StarContainer,
    StarData,
    StarNumber,
    StarsContainer,
    ThreeStarsContainer,
} from './styles';

type Props = {
    game: LandingGame;
};

type StarProps = {
    star: ThreeStar;
};

const formatPlayerInfo = (sweaterNumber: number, teamAbbrev: string, positionCode: string): ReactNode => (
    <FormattedMessage
        description="Formatting a player's information including their sweater number, team abbreviation, and position"
        defaultMessage="#{sweaterNumber} • {team} • {position}"
        values={{
            sweaterNumber: sweaterNumber,
            team: teamAbbrev,
            position: (
                <CoreUtils.SafeFormattedMessage message={CommonMessages.playerInfoDescriptions.long[positionCode]} />
            ),
        }}
    />
);

const formatPlayerStats = (
    goalCount: number | undefined,
    assistCount: number | undefined,
    pointCount: number | undefined
): ReactNode => (
    <FormattedMessage
        description="A player's goal, assists, and points statistics"
        defaultMessage="{goalMessage}: {goalCount} | {assistMessage}: {assistCount} | {pointsMessage}: {pointCount}"
        values={{
            goalMessage: (
                <FormattedMessage {...CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.goals]} />
            ),
            assistMessage: (
                <FormattedMessage {...CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.assists]} />
            ),
            pointsMessage: (
                <FormattedMessage {...CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.points]} />
            ),
            goalCount,
            assistCount,
            pointCount,
        }}
    />
);

const formatGoalieStats = (goalsAgainstAverage: number | undefined, savePercentage: number | undefined): ReactNode => (
    <FormattedMessage
        description="A goalie's goals against average, and save percentage statistics"
        defaultMessage="{goalsAgainstAverageMessage}: {goalsAgainstAverage} | {savePercentageMessage}: {savePercentage}"
        values={{
            goalsAgainstAverageMessage: (
                <FormattedMessage
                    {...CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.goalsAgainstAvg]}
                />
            ),
            savePercentageMessage: (
                <FormattedMessage {...CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.savePctg]} />
            ),
            goalsAgainstAverage: statFormatter.displayValueAs('goalsAgainstAverage')(goalsAgainstAverage, true),
            savePercentage: statFormatter.displayValueAs('savePctg')(savePercentage),
        }}
    />
);

const Star: FC<StarProps> = ({ star }) => {
    const isWebview = useIsWebview();
    const screenSize = useSizeWithThrottle();
    const theme = useTheme();
    const isExtraLarge = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.extraLarge;

    return (
        <StarContainer key={star.playerId}>
            <Headshot
                type={star.position === PlayerPosition.goalie ? headshotTypes.GOALIE : headshotTypes.SKATER}
                urlOverride={star.headshot}
                size={isExtraLarge ? 100 : 64}
                backgroundColor={star.teamAbbrev ? theme.Team[star.teamAbbrev]?.gamecenter : theme.Surface.secondary}
            />
            <StarData>
                <NameContainer href={getPlayerHref(star.playerId, isWebview)} rel="noreferrer">
                    {parseValueFromLocalizedAttribute(star.name)}
                </NameContainer>
                <PlayerInfo>{formatPlayerInfo(star.sweaterNo, star.teamAbbrev, star.position)}</PlayerInfo>
                <PlayerInfo>
                    {star.position === PlayerPosition.goalie
                        ? formatGoalieStats(star.goalsAgainstAverage, star.savePctg)
                        : formatPlayerStats(star.goals, star.assists, star.points)}
                </PlayerInfo>
            </StarData>
            <StarNumber>{star.star}</StarNumber>
        </StarContainer>
    );
};

const ThreeStars: FC<Props> = ({ game }) => {
    return game.summary.threeStars.length ? (
        <ThreeStarsContainer>
            <SectionHeader aria-level={2}>
                <FormattedMessage
                    description="Header describing the three stars of a game"
                    defaultMessage="Three Stars of the Game"
                />
            </SectionHeader>
            <StarsContainer>
                {game.summary.threeStars.map((star) => (
                    <Star star={star} key={star.playerId} />
                ))}
            </StarsContainer>
        </ThreeStarsContainer>
    ) : (
        <></>
    );
};

export default ThreeStars;
